import React, { useCallback, useEffect, useState } from 'react';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  LinearProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';

import { Translate, NotificationBanner } from '@hydra/common/components';
import Actions from '@hydra/routes/account/routes/security/components/Actions';

import * as fetcher from '@hydra/utils/fetcher';
import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    borderRadius: theme.shape.borderRadius * 2.5
  },
  thLabel: {
    fontWeight: theme.typography.fontWeightSemibold,
    cursor: 'default'
  },
  hiddenXs: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'table-cell'
    }
  },
  actions: {
    textAlign: 'end'
  },
  alert: {
    marginBottom: theme.spacing(2)
  },
  chip: {
    color: theme.palette.grey[500],
    background: theme.palette.grey[200]
  }
}));

interface ISessionData {
  key: string
  ip: string
  browser: {
    type: string
    name: string
    version: string
    engine: string
    engineVersion: string
  } | null
  os: {
    name: string
    version: string
    platform: string
  } | null
  device: {
    type: string
    brand: string
    model: string
  } | null
  location: {
    cityName: string
    countryCode: string
    latitude: number
    longitude: number
    timeZone: string
  } | null
}
export default function SessionTable () {
  const classes = useStyles();
  const { translate } = useI18n();
  const [loading, setLoading] = useState<boolean>(false);
  const [sessions, setSessions] = useState<ISessionData[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const json = await fetcher.get('/api/user/active-sessions').json();
      setSessions(json);
    } catch (error: any) {
      setError('error_500');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleLogoutSession = useCallback(async (id: string) => {
    try {
      await fetcher.post(`/api/user/${id}/logout`).json();
      await loadSessions();
    } catch (error: any) {
      setError('error_500');
    }
  }, [loadSessions]);

  return (
    <>
      {
        error && (
          <div className={classes.alert}>
            <NotificationBanner
              severity='error'
              title={translate(error)}
              dismissable
            />
          </div>
        )
      }
      <Paper className={classes.root} elevation={0} variant='outlined'>
        {
          loading
            ? <InitialLoadingSessions />
            : (
              <Table>
                <TableHeader />
                <TableBody>
                  {
                    sessions.map((session: any) => (
                      <TableRow key={session.key}>
                        <TableCell>
                          {session.ip || <Translate content='account_security_not_available' />}
                        </TableCell>
                        <TableCell>
                          {
                            (session.browser?.name && session.os?.name)
                              ? (
                                <>
                                  <Translate content='account_security_browser_os' with={{ browser: session.browser.name, os: session.os.name }} />
                                  {
                                    session.device?.brand && ` (${session.device.brand} ${session.device?.model ? session.device.model : session.device.type})`
                                  }
                                </>
                                )
                              : <Translate content='account_security_not_available' />
                          }
                        </TableCell>
                        <TableCell>
                          {
                            (session.location?.countryCode)
                              ? <Translate content={`country_name_${session.location.countryCode.toUpperCase()}`} />
                              : <Translate content='account_security_not_available' />
                          }
                        </TableCell>
                        <TableCell className={classes.actions}>
                          {
                            session.currentSession
                              ? <Chip label={<Translate content='account_security_current_session' />} color='secondary' size='small' className={classes.chip} />
                              : <Actions onDelete={() => handleLogoutSession(session.key)} />
                          }
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
              )
        }
        {
          loading && <LinearProgress />
        }
      </Paper>
    </>
  );
}

function InitialLoadingSessions () {
  return (
    <Table>
      <TableHeader />
      <TableBody>
        {
          new Array(2).fill(null).map((n, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton width={50} />
              </TableCell>
              <TableCell>
                <Skeleton width={60} />
              </TableCell>
              <TableCell>
                <Skeleton width={60} />
              </TableCell>
              <TableCell />
            </TableRow>
          ))
        }
      </TableBody>
    </Table>
  );
}

function TableHeader () {
  const classes = useStyles();
  return (
    <TableHead>
      <TableRow>
        <TableCell size='small'>
          <Typography
            noWrap
            variant='body2'
            component='span'
            className={classes.thLabel}
          >
            <Translate content='account_security_ip' />
          </Typography>
        </TableCell>
        <TableCell size='small'>
          <Typography
            noWrap
            variant='body2'
            component='span'
            className={classes.thLabel}
          >
            <Translate content='account_security_browser' />
          </Typography>
        </TableCell>
        <TableCell size='small'>
          <Typography
            noWrap
            variant='body2'
            component='span'
            className={classes.thLabel}
          >
            <Translate content='account_security_country' />
          </Typography>
        </TableCell>
        <TableCell size='small' />
      </TableRow>
    </TableHead>
  );
}
